import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import type {
  AppSettings,
  Category,
  Expense,
} from "@/models/types";
import { categories as seedCategories } from "@/mocks/categories";
import { expenses as seedExpenses, defaultSettings } from "@/mocks/expenses";

interface AppState {
  expenses: Expense[];
  categories: Category[];
  settings: AppSettings;
}

type Action =
  | { type: "ADD_EXPENSE"; payload: Expense }
  | { type: "UPDATE_EXPENSE"; payload: Expense }
  | { type: "DELETE_EXPENSE"; payload: string }
  | { type: "ADD_CATEGORY"; payload: Category }
  | { type: "UPDATE_CATEGORY"; payload: Category }
  | { type: "DELETE_CATEGORY"; payload: string }
  | { type: "UPDATE_SETTINGS"; payload: Partial<AppSettings> }
  | {
      type: "IMPORT_DATA";
      payload: {
        expenses?: Expense[];
        categories?: Category[];
        settings?: Partial<AppSettings>;
      };
    };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "ADD_EXPENSE":
      return { ...state, expenses: [action.payload, ...state.expenses] };
    case "UPDATE_EXPENSE":
      return {
        ...state,
        expenses: state.expenses.map((e) =>
          e.id === action.payload.id ? action.payload : e,
        ),
      };
    case "DELETE_EXPENSE":
      return {
        ...state,
        expenses: state.expenses.filter((e) => e.id !== action.payload),
      };
    case "ADD_CATEGORY":
      return { ...state, categories: [...state.categories, action.payload] };
    case "UPDATE_CATEGORY":
      return {
        ...state,
        categories: state.categories.map((c) =>
          c.id === action.payload.id ? action.payload : c,
        ),
      };
    case "DELETE_CATEGORY":
      return {
        ...state,
        categories: state.categories.filter((c) => c.id !== action.payload),
      };
    case "UPDATE_SETTINGS":
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };
    case "IMPORT_DATA":
      return {
        expenses: action.payload.expenses ?? state.expenses,
        categories: action.payload.categories ?? state.categories,
        settings: { ...state.settings, ...(action.payload.settings ?? {}) },
      };
    default:
      return state;
  }
}

interface ExpenseContextValue {
  expenses: Expense[];
  categories: Category[];
  settings: AppSettings;
  addExpense: (expense: Expense) => void;
  updateExpense: (expense: Expense) => void;
  deleteExpense: (id: string) => void;
  addCategory: (category: Category) => void;
  updateCategory: (category: Category) => void;
  deleteCategory: (id: string) => void;
  updateSettings: (settings: Partial<AppSettings>) => void;
  importData: (data: {
    expenses?: Expense[];
    categories?: Category[];
    settings?: Partial<AppSettings>;
  }) => void;
}

const ExpenseContext = createContext<ExpenseContextValue | undefined>(
  undefined,
);

export function ExpenseProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    expenses: seedExpenses,
    categories: seedCategories,
    settings: defaultSettings,
  });

  useEffect(() => {
    const root = document.documentElement;
    if (state.settings.theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [state.settings.theme]);

  const value = useMemo<ExpenseContextValue>(
    () => ({
      expenses: state.expenses,
      categories: state.categories,
      settings: state.settings,
      addExpense: (expense) => dispatch({ type: "ADD_EXPENSE", payload: expense }),
      updateExpense: (expense) =>
        dispatch({ type: "UPDATE_EXPENSE", payload: expense }),
      deleteExpense: (id) => dispatch({ type: "DELETE_EXPENSE", payload: id }),
      addCategory: (category) =>
        dispatch({ type: "ADD_CATEGORY", payload: category }),
      updateCategory: (category) =>
        dispatch({ type: "UPDATE_CATEGORY", payload: category }),
      deleteCategory: (id) => dispatch({ type: "DELETE_CATEGORY", payload: id }),
      updateSettings: (settings) =>
        dispatch({ type: "UPDATE_SETTINGS", payload: settings }),
      importData: (data) =>
        dispatch({ type: "IMPORT_DATA", payload: data }),
    }),
    [state],
  );

  return (
    <ExpenseContext.Provider value={value}>{children}</ExpenseContext.Provider>
  );
}

export function useExpenses(): ExpenseContextValue {
  const ctx = useContext(ExpenseContext);
  if (!ctx) {
    throw new Error("useExpenses debe usarse dentro de ExpenseProvider");
  }
  return ctx;
}